/**
 * Live Balance Helper
 *
 * Computes actual outstanding balances for customers, vendors, and stock for
 * items by reading from `subTransactions` and `stockFlow` directly, instead
 * of relying on the stale denormalized `account.currentBalance` /
 * `vendor.currentBalance` / `item.currentStock` snapshots.
 *
 * Mirrors the math used by Reports → Customer Balances / Vendor Balances /
 * Inventory Balances, so the master pages always agree with the reports.
 */

import { collection, getDocs, query, where } from 'firebase/firestore';
import { db } from '../config/firebase';
import { activeFyId, inActiveFy } from './stockFlowHelper';
import { recordReads } from './readTelemetry';

// stockFlow rows that count toward "live" stock (matches stockFlowHelper).
// 'Invoiced' included so a DC/GRN that posted stock (DC-out / GRN-in modes) keeps
// counting after it's converted and flipped to 'Invoiced'.
const ACTIVE_STOCK_STATUSES = [
  'Posted',
  'Received',
  'Dispatched',
  'Delivered',
  'Invoiced',
  'Completed',
  'InProgress',
  'Partial',
  'Paid',
  // INV-01 fix (2026-09-30): Send Inventory OUT leg
  'Sent',
  'Partially Received',
];

const isAllBranches = (branchId) => !branchId || branchId === 'all';

const matchesBranch = (entry, branchId) => {
  if (isAllBranches(branchId)) return true;
  return entry.branchId === branchId;
};

// Drafts only count when they belong to Opening Balance transactions —
// matches the rule used by the Customer/Vendor Balances reports so OBs are
// always reflected even before the user posts them.
const includeSubTransaction = (entry) => {
  if (entry.status === 'Posted') return true;
  if (entry.status === 'Draft' && entry.tranTypeCode === 'OB') return true;
  return false;
};

const fetchPartyAccounts = async (companyId, entityType) => {
  const accountsRef = collection(db, 'companies', companyId, 'accounts');
  const q = query(accountsRef, where('linkedEntityType', '==', entityType));
  const snap = await getDocs(q);
  recordReads(companyId, snap.size);
  return snap.docs.map((d) => ({ id: d.id, ...d.data() }));
};

const fetchSubTransactions = async (companyId, financialYearId) => {
  const ref = collection(db, 'companies', companyId, 'subTransactions');
  const conditions = [where('status', 'in', ['Posted', 'Draft'])];
  if (financialYearId) {
    conditions.push(where('financialYearId', '==', financialYearId));
  }
  const q = query(ref, ...conditions);
  const snap = await getDocs(q);
  recordReads(companyId, snap.size);
  return snap.docs.map((d) => ({ id: d.id, ...d.data() }));
};

const computePartyBalances = (accounts, subTxns, direction, branchId) => {
  const accountIdToParty = {};
  const balances = {};
  accounts.forEach((a) => {
    if (a.linkedEntityId) {
      accountIdToParty[a.id] = a.linkedEntityId;
      balances[a.linkedEntityId] = 0;
    }
  });

  subTxns.forEach((t) => {
    const partyId = accountIdToParty[t.accountId];
    if (!partyId) return;
    if (!includeSubTransaction(t)) return;
    if (!matchesBranch(t, branchId)) return;
    const dr = Number(t.debitAmount) || 0;
    const cr = Number(t.creditAmount) || 0;
    // Customers (asset)    → positive balance = amount we will RECEIVE
    // Vendors  (liability) → positive balance = amount we will PAY
    balances[partyId] += direction === 'asset' ? dr - cr : cr - dr;
  });

  return balances;
};

/**
 * Returns `{ [customerId]: balanceNumber }`. Positive = outstanding receivable.
 */
export const loadCustomerBalances = async (
  companyId,
  branchId,
  financialYearId
) => {
  if (!companyId) return {};
  try {
    const [accounts, subTxns] = await Promise.all([
      fetchPartyAccounts(companyId, 'customer'),
      fetchSubTransactions(companyId, financialYearId),
    ]);
    return computePartyBalances(accounts, subTxns, 'asset', branchId);
  } catch (error) {
    console.error('loadCustomerBalances failed:', error);
    return {};
  }
};

/**
 * Lean single-customer balance — used by POS where loading every customer's
 * ledger would be wasteful. Reads only the one customer's account rows.
 * Returns a number (positive = outstanding receivable), or 0 if none/none-linked.
 */
export const getCustomerBalance = async (
  companyId,
  customerId,
  branchId,
  financialYearId
) => {
  if (!companyId || !customerId) return 0;
  try {
    // Find the customer's linked ledger account.
    const accountsRef = collection(db, 'companies', companyId, 'accounts');
    const accSnap = await getDocs(
      query(
        accountsRef,
        where('linkedEntityType', '==', 'customer'),
        where('linkedEntityId', '==', customerId)
      )
    );
    if (accSnap.empty) return 0;
    const accountId = accSnap.docs[0].id;

    // Sum only this account's subTransactions.
    const subRef = collection(db, 'companies', companyId, 'subTransactions');
    const conditions = [
      where('accountId', '==', accountId),
      where('status', 'in', ['Posted', 'Draft']),
    ];
    if (financialYearId) {
      conditions.push(where('financialYearId', '==', financialYearId));
    }
    const subSnap = await getDocs(query(subRef, ...conditions));

    let balance = 0;
    subSnap.docs.forEach((d) => {
      const t = d.data();
      if (!includeSubTransaction(t)) return;
      if (!matchesBranch(t, branchId)) return;
      balance += (Number(t.debitAmount) || 0) - (Number(t.creditAmount) || 0);
    });
    return balance;
  } catch (error) {
    console.error('getCustomerBalance failed:', error);
    return 0;
  }
};

/**
 * Returns `{ [vendorId]: balanceNumber }`. Positive = outstanding payable.
 */
export const loadVendorBalances = async (
  companyId,
  branchId,
  financialYearId
) => {
  if (!companyId) return {};
  try {
    const [accounts, subTxns] = await Promise.all([
      fetchPartyAccounts(companyId, 'vendor'),
      fetchSubTransactions(companyId, financialYearId),
    ]);
    return computePartyBalances(accounts, subTxns, 'liability', branchId);
  } catch (error) {
    console.error('loadVendorBalances failed:', error);
    return {};
  }
};

/* ── Shared-ledger variants (2026-08-02, Firebase cost) ──────────────────────
 * The dashboard KPI live fallback needs customer balances, vendor balances AND
 * the raw ledger. Calling loadCustomerBalances + loadVendorBalances + a raw
 * fetch downloaded the SAME full-year subTransactions THREE times. These let a
 * caller download the ledger ONCE and reuse it for every computation — the math
 * is the same computePartyBalances used above, so the figures are identical.
 */

/** The exact ledger query loadCustomerBalances / loadVendorBalances run. */
export const fetchLedgerRowsForBalances = async (companyId, financialYearId) => {
  if (!companyId) return [];
  return fetchSubTransactions(companyId, financialYearId);
};

/** Customer balances from PRE-FETCHED accounts + ledger rows. `accounts` may be
 *  the whole chart of accounts — customer accounts are picked out here, exactly
 *  as the linkedEntityType == 'customer' query does. */
export const computeCustomerBalancesFrom = (accounts, subTxns, branchId) =>
  computePartyBalances(
    (accounts || []).filter((a) => a.linkedEntityType === 'customer'),
    subTxns || [],
    'asset',
    branchId
  );

/** Vendor balances from PRE-FETCHED accounts + ledger rows (see above). */
export const computeVendorBalancesFrom = (accounts, subTxns, branchId) =>
  computePartyBalances(
    (accounts || []).filter((a) => a.linkedEntityType === 'vendor'),
    subTxns || [],
    'liability',
    branchId
  );

/**
 * Returns `{ [itemId]: quantityNumber }` — live on-hand quantity in base units.
 */
export const loadItemStocks = async (companyId, branchId) => {
  if (!companyId) return {};
  try {
    const ref = collection(db, 'companies', companyId, 'stockFlow');
    const q = query(ref, where('status', 'in', ACTIVE_STOCK_STATUSES));
    const snap = await getDocs(q);
    recordReads(companyId, snap.size);
    const stocks = {};
    // Active financial year only — see stockFlowHelper.inActiveFy. Counting last
    // year's opening stock as well as this year's would double every item.
    const fy = activeFyId();
    snap.docs.forEach((d) => {
      const t = d.data();
      if (!t.itemId) return;
      if (!inActiveFy(t, fy)) return;
      if (!matchesBranch(t, branchId)) return;
      const qty = Number(t.quantity) || 0;
      if (stocks[t.itemId] === undefined) stocks[t.itemId] = 0;
      if (t.direction === 'in') stocks[t.itemId] += qty;
      else if (t.direction === 'out') stocks[t.itemId] -= qty;
    });
    return stocks;
  } catch (error) {
    console.error('loadItemStocks failed:', error);
    return {};
  }
};

export default {
  loadCustomerBalances,
  getCustomerBalance,
  loadVendorBalances,
  loadItemStocks,
  fetchLedgerRowsForBalances,
  computeCustomerBalancesFrom,
  computeVendorBalancesFrom,
};
